// Generated by ReScript, PLEASE EDIT WITH CARE

import * as I18n from "../../primitives/I18n.res.js";
import * as Button from "../../resources/actions/Button.res.js";
import * as Tooltip from "../../resources/overlays/Tooltip.res.js";
import * as TableView from "../../resources/tables/TableView.res.js";
import * as TextStyle from "../../resources/typography/TextStyle.res.js";
import * as TooltipIcon from "../../resources/overlays/TooltipIcon.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_object from "@rescript/runtime/lib/es6/Primitive_object.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as PromotionEditDiscountedProduct from "./PromotionEditDiscountedProduct.res.js";
import * as LegacyProductReferenceTableCell from "../Product/LegacyProductReferenceTableCell.res.js";
import * as PromotionEditDiscountedProductDiscountTableCell from "./PromotionEditDiscountedProductDiscountTableCell.res.js";

function getRetailPriceValue(retailPrices, selectedPriceName) {
  return Stdlib_Option.flatMap(retailPrices, prices => Stdlib_Option.map(prices.find(price => price.name === selectedPriceName), price => price.value));
}

function keyExtractor(row) {
  return row.cku;
}

let Row = {
  keyExtractor: keyExtractor
};

function tableColumns(editable, selectedPriceName, allowRowReplication, onRequestDispatch) {
  return [
    {
      key: "reference",
      name: I18n.t("Product & description"),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 250
        },
        width: {
          NAME: "pct",
          VAL: 40
        },
        margin: "large"
      },
      render: param => {
        let match = param.data;
        return JsxRuntime.jsx(LegacyProductReferenceTableCell.make, {
          cku: match.cku,
          name: match.name,
          description: match.description,
          errorMessage: param.errorMessage,
          openNewTab: true
        });
      }
    },
    {
      key: "purchase-price",
      name: I18n.t("Purchase price") + " " + I18n.t("VAT excl."),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 120
        }
      },
      render: param => {
        let match = param.data;
        return JsxRuntime.jsx(TextStyle.make, {
          children: I18n.currencyFormat(undefined, "EUR", 3, 3, match.purchasePrice) + Stdlib_Option.mapOr(match.bulkUnit, "", unit => `/` + unit)
        });
      }
    },
    {
      key: "retail-price",
      name: I18n.t("Retail price") + " " + I18n.t("VAT incl."),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 120
        }
      },
      render: param => {
        let match = param.data;
        let name = match.name;
        let cku = match.cku;
        if (selectedPriceName === undefined) {
          return JsxRuntime.jsx(TooltipIcon.make, {
            children: JsxRuntime.jsx(Tooltip.Span.make, {
              text: I18n.t("Please select the price list first to see the retail price.")
            }),
            variation: "info"
          }, cku + name);
        }
        let retailPriceValue = getRetailPriceValue(match.retailPrices, selectedPriceName);
        if (retailPriceValue !== undefined) {
          return JsxRuntime.jsx(TextStyle.make, {
            children: I18n.currencyFormat(undefined, "EUR", undefined, undefined, retailPriceValue) + Stdlib_Option.mapOr(match.bulkUnit, "", unit => `/` + unit)
          }, cku + name);
        } else {
          return JsxRuntime.jsx(TooltipIcon.make, {
            children: JsxRuntime.jsx(Tooltip.Span.make, {
              text: I18n.t("One of the promotional campaign linked shops does not have a retail price entered for that product on the selected price list.")
            }),
            variation: "info"
          }, cku + name);
        }
      }
    },
    {
      key: "discount",
      name: I18n.t("Discount"),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 120
        }
      },
      render: param => {
        let match = param.data;
        let cku = match.cku;
        let onRequestUpdate = current => onRequestDispatch({
          TAG: "ProductDiscountUpdated",
          _0: cku,
          _1: current.amount,
          _2: current.kind
        });
        let onRequestReplicate = () => onRequestDispatch({
          TAG: "ProductDiscountReplicated",
          _0: cku
        });
        return JsxRuntime.jsx(PromotionEditDiscountedProductDiscountTableCell.make, {
          editable: editable,
          disabled: Stdlib_Option.isNone(selectedPriceName),
          discount: match.discount,
          bulkUnit: match.bulkUnit,
          onRequestUpdate: onRequestUpdate,
          onRequestReplicate: allowRowReplication ? onRequestReplicate : undefined
        });
      }
    },
    {
      key: "discounted-price",
      name: I18n.t("Discounted price") + " " + I18n.t("VAT incl."),
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 120
        }
      },
      render: param => {
        let match = param.data;
        let match$1 = match.discount;
        let kind = match$1.kind;
        let amount = match$1.amount;
        let retailPriceValue = getRetailPriceValue(match.retailPrices, selectedPriceName);
        let discountedPrice = Stdlib_Option.map(retailPriceValue, value => PromotionEditDiscountedProduct.ProductPrice.makeDiscounted(value, amount, kind));
        return JsxRuntime.jsx(TextStyle.make, {
          children: I18n.currencyFormat(undefined, "EUR", undefined, undefined, Stdlib_Option.getOr(discountedPrice, 0)) + Stdlib_Option.mapOr(match.bulkUnit, "", unit => `/` + unit),
          variation: retailPriceValue !== undefined && retailPriceValue !== discountedPrice ? (
              Primitive_object.lessthan(discountedPrice, 0) ? "negative" : (
                  retailPriceValue !== 0 ? "neutral" : "normal"
                )
            ) : "normal"
        });
      }
    },
    {
      key: "actions",
      layout: {
        minWidth: {
          NAME: "px",
          VAL: 80
        },
        width: {
          NAME: "px",
          VAL: 80
        },
        alignX: "flexEnd",
        sticky: true
      },
      render: param => {
        if (!editable) {
          return null;
        }
        let cku = param.data.cku;
        return JsxRuntime.jsx(Button.make, {
          shape: "pill",
          icon: "delete",
          onPress: () => onRequestDispatch({
            TAG: "ProductRemoved",
            _0: cku
          })
        });
      }
    }
  ];
}

let make = TableView.make;

export {
  getRetailPriceValue,
  Row,
  tableColumns,
  make,
}
/* I18n Not a pure module */
